import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { OrganizationSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [activeSection, setActiveSection] = useState<'intro' | 'reputation' | 'contact'>('intro');

  return (
    <>
      <ScrollToTop />
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 开云官网"
        description="了解开云平台的品牌故事、核心优势、行业信誉与联系方式。开云官网致力于为用户提供安全可靠的在线娱乐体验。"
        url="https://www.kaiyun.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.kaiyun.com/' },
          { name: '关于我们', url: 'https://www.kaiyun.com/about' },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <main>
            <section className="relative py-14 md:py-20 bg-background-100">
              <div className="absolute inset-0 opacity-20">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20dark%20abstract%20geometric%20background%20with%20subtle%20golden%20gradient%20lines%2C%20very%20clean%20and%20simple%20composition%2C%20barely%20visible%20patterns%2C%20warm%20dark%20tone%2C%20professional%20corporate%20texture%2C%20no%20text%2C%20no%20logos%2C%20suitable%20for%20about%20page%20subtle%20background&width=1600&height=800&seq=about-hero-2026&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于<strong className="text-primary-500">开云</strong>
                </h1>
                <p className="text-secondary-600 max-w-2xl mx-auto">
                  开云平台，致力于为全球用户提供安全、专业、极致的在线娱乐体验
                </p>

                <div className="inline-flex bg-background-50 rounded-xl border border-background-200/50 p-1.5 mt-10">
                  {[
                    { key: 'intro', label: '品牌介绍' },
                    { key: 'reputation', label: '平台信誉' },
                    { key: 'contact', label: '联系方式' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveSection(tab.key as 'intro' | 'reputation' | 'contact');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeSection === tab.key
                          ? 'bg-primary-500 text-background-50 shadow-sm'
                          : 'text-secondary-600 hover:text-foreground-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {activeSection === 'intro' && <BrandIntroSection />}
            {activeSection === 'reputation' && <ReputationSection />}
            {activeSection === 'contact' && <ContactSection />}
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}

function BrandIntroSection() {
  const milestones = [
    { year: '2015', event: '开云平台正式成立，以技术创新为核心驱动力' },
    { year: '2017', event: '平台用户突破100万，成为行业知名品牌' },
    { year: '2019', event: '获得国际博彩行业协会认证，拓展全球市场' },
    { year: '2021', event: '用户突破500万，推出全新VIP会员体系' },
    { year: '2023', event: '完成安全体系全面升级，引入AI智能风控系统' },
    { year: '2025', event: '荣获年度最佳在线娱乐平台大奖，行业领先地位确立' },
  ];

  const values = [
    {
      icon: 'ri-shield-star-line',
      title: '安全至上',
      desc: '我们始终将用户安全放在首位，采用先进的加密技术与风控体系，为每一位用户提供可靠的保护。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户为本',
      desc: '以用户需求为导向，持续优化产品与服务，打造极致的用户体验。',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: '创新驱动',
      desc: '不断探索前沿技术，将创新融入产品设计的每个环节，引领行业发展方向。',
    },
    {
      icon: 'ri-hand-heart-line',
      title: '责任担当',
      desc: '积极履行社会责任，倡导健康娱乐理念，推动行业可持续发展。',
    },
  ];

  return (
    <>
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <div className="w-14 h-14 rounded-xl bg-primary-500/10 flex items-center justify-center mb-5">
                <i className="ri-building-4-line text-2xl text-primary-500 w-7 h-7 flex items-center justify-center"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                品牌<strong className="text-primary-500">故事</strong>
              </h2>
              <div className="space-y-4 text-secondary-600 leading-relaxed">
                <p>
                  开云平台成立于2015年，由一群怀揣梦想的技术精英与行业专家共同创立。自创立之初，开云便确立了"安全、专业、创新"的核心价值观，致力于为全球用户打造卓越的在线娱乐体验。
                </p>
                <p>
                  经过多年深耕，开云平台已发展成为行业领先的综合性在线娱乐平台，汇聚了体育竞技、真人视讯、电子游艺、彩票游戏等多元化产品，服务覆盖全球超过500万注册用户。
                </p>
                <p>
                  开云始终坚持技术创新与用户体验并重的发展策略，持续投入研发资源，在安全防护、支付系统、AI应用等领域取得了多项突破性成果。平台自主研发的智能风控系统，能够实时识别并防范潜在风险，为用户资金安全提供坚实保障。
                </p>
                <p>
                  展望未来，开云将继续秉持初心，以技术创新为驱动，以用户满意为目标，不断探索在线娱乐的更多可能，为全球用户带来更加安全、便捷、精彩的娱乐体验。
                </p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-80 lg:h-[500px]">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20dark%20abstract%20corporate%20illustration%20with%20golden%20geometric%20architecture%20shapes%2C%20premium%20modern%20building%20concept%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20professional%20corporate%20brand%20theme%2C%20cinematic%20lighting%2C%20luxurious%20atmospheric%20composition&width=800&height=1000&seq=about-brand-2026&orientation=portrait"
                alt="开云品牌"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              核心<strong className="text-primary-500">价值观</strong>
            </h2>
            <p className="text-secondary-600 max-w-xl mx-auto">
              开云平台始终坚守的核心价值理念，指引我们不断前行
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="p-6 rounded-xl bg-background-50 border border-background-200/50 text-center">
                <div className="w-14 h-14 rounded-xl bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
                  <i className={`${v.icon} text-2xl text-primary-500 w-7 h-7 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-secondary-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              发展<strong className="text-primary-500">历程</strong>
            </h2>
            <p className="text-secondary-600">开云平台从创立到行业领先的发展之路</p>
          </div>

          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2 hidden md:block"></div>

            <div className="flex flex-col gap-6">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex items-start gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`hidden md:flex flex-1 ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                    <div className={`p-4 rounded-xl bg-background-100 border border-background-200/50 max-w-xs ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <p className="text-sm text-secondary-600">{m.event}</p>
                    </div>
                  </div>

                  <div className="relative z-10 w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-background-50">{m.year.slice(2)}</span>
                  </div>

                  <div className="flex-1 md:hidden">
                    <div className="p-4 rounded-xl bg-background-100 border border-background-200/50">
                      <p className="text-sm text-secondary-600">{m.event}</p>
                    </div>
                  </div>

                  <div className={`hidden md:block flex-1 ${i % 2 === 0 ? '' : ''}`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ReputationSection() {
  const certs = [
    {
      icon: 'ri-shield-check-line',
      title: '国际博彩行业协会认证',
      desc: '通过IGA严格审核，获得铂金级认证，平台安全性与合规性达到国际最高标准。',
    },
    {
      icon: 'ri-lock-2-line',
      title: 'SSL安全加密认证',
      desc: '采用128位SSL加密技术，通过国际权威安全机构认证，确保数据传输与存储安全。',
    },
    {
      icon: 'ri-verified-badge-line',
      title: '责任博彩IRGA认证',
      desc: '责任博彩体系通过国际责任博彩协会认证，倡导健康娱乐理念。',
    },
    {
      icon: 'ri-bank-card-line',
      title: 'PCI DSS支付安全认证',
      desc: '支付系统符合PCI DSS国际支付卡行业数据安全标准，保障用户支付信息安全。',
    },
    {
      icon: 'ri-government-line',
      title: '合法运营牌照',
      desc: '持有国际权威机构颁发的合法在线娱乐运营牌照，受法律严格监管。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'ISO 9001服务质量管理',
      desc: '客户服务体系通过ISO 9001质量管理认证，确保高标准服务质量。',
    },
  ];

  const stats = [
    { value: '500万+', label: '注册用户' },
    { value: '99.9%', label: '平台可用率' },
    { value: '10万+', label: '日活跃用户' },
    { value: '50亿+', label: '年交易额（元）' },
  ];

  return (
    <>
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              资质<strong className="text-primary-500">认证</strong>
            </h2>
            <p className="text-secondary-600 max-w-xl mx-auto">
              开云平台拥有多项国际权威认证，为您提供安全可靠的娱乐环境
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {certs.map((cert) => (
              <div key={cert.title} className="p-6 rounded-xl bg-background-100 border border-background-200/50 flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className={`${cert.icon} text-xl text-primary-500 w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{cert.title}</h3>
                  <p className="text-sm text-secondary-600 leading-relaxed">{cert.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">{stat.value}</div>
                <div className="text-sm text-secondary-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
            用户<strong className="text-primary-500">口碑</strong>
          </h2>
          <p className="text-secondary-600 mb-10">来自真实用户的高度评价</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: '张先生', role: 'VIP会员 3年', comment: '开云平台是我用过最稳定的平台，充值提现都很快，客服响应也很及时。三年了一直在这里玩，值得信赖。' },
              { name: '李先生', role: '活跃用户', comment: '第一次接触在线娱乐就是开云，界面简洁好用，游戏种类多。最重要的是安全性让人放心，资金从来没有出过问题。' },
              { name: '王女士', role: '黄金会员 1年', comment: '开云的VIP服务真的很贴心，专属客服经理随时都能联系上。提现优先处理这一点特别赞，大额提现也很快到账。' },
            ].map((review, index) => (
              <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200/50 text-left">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-secondary-600 leading-relaxed mb-4">{review.comment}</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-500">{review.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                    <p className="text-xs text-secondary-500">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const formUrl = 'https://readdy.ai/api/form/d9rfprnnahb7382ovlqg';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              联系<strong className="text-primary-500">我们</strong>
            </h2>
            <p className="text-secondary-600 max-w-xl mx-auto">
              如有任何疑问或建议，欢迎通过以下方式与我们联系
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 flex flex-col gap-5">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="w-11 h-11 rounded-lg bg-primary-500/10 flex items-center justify-center mb-3">
                  <i className="ri-customer-service-2-line text-xl text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1">在线客服</h3>
                <p className="text-sm text-secondary-600">7×24小时全天候在线服务</p>
                <p className="text-sm text-primary-500 mt-2 font-medium">support@kaiyun.com</p>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="w-11 h-11 rounded-lg bg-primary-500/10 flex items-center justify-center mb-3">
                  <i className="ri-phone-line text-xl text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1">VIP专线</h3>
                <p className="text-sm text-secondary-600">VIP会员专属服务热线</p>
                <p className="text-sm text-primary-500 mt-2 font-medium">400-888-KAIYUN</p>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="w-11 h-11 rounded-lg bg-primary-500/10 flex items-center justify-center mb-3">
                  <i className="ri-wechat-line text-xl text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1">官方微信</h3>
                <p className="text-sm text-secondary-600">关注开云官方微信公众号</p>
                <p className="text-sm text-primary-500 mt-2 font-medium">开云平台官方服务号</p>
              </div>
            </div>

            <div className="lg:col-span-2 p-8 rounded-2xl bg-background-100 border border-background-200/50">
              <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">发送消息</h3>

              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-500/10 flex items-center justify-center mb-4">
                    <i className="ri-check-line text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送！</h4>
                  <p className="text-secondary-600">感谢您的留言，我们将在24小时内回复您。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-2.5 rounded-lg border border-primary-500/40 text-primary-500 font-medium text-sm hover:bg-primary-500/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">您的姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">电子邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all duration-200"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-2">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all duration-200"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all duration-200 resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-secondary-500 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-500/20">
                      <p className="text-sm text-accent-600">{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}